import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  Globe, Leaf, Camera, Trophy, Sparkles, X, ChevronRight, 
  ChevronLeft, MapPin, Palette, Users, RotateCcw
} from "lucide-react";

const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Welcome to ROOTSPACE!",
    description: "Let's take a quick tour to help you get started with your garden planning journey. We'll show you 5 key features that make ROOTSPACE special.",
    icon: Leaf,
    color: "from-green-500 to-emerald-600",
    position: "center",
    highlight: null
  },
  {
    id: "templates",
    title: "Explore World Gardens",
    description: "Browse 20 beautiful garden templates from famous locations around the world - from the Palace of Versailles to Japanese Zen gardens. Each includes local climate info, soil types, and suggested plants.",
    icon: Globe,
    color: "from-blue-500 to-cyan-600",
    position: "center",
    action: "Go to Design Studio → Click 'Explore' tab",
    tip: "Perfect for beginners! Start with a template and customize it."
  },
  {
    id: "design",
    title: "Visual Design Studio",
    description: "Create stunning garden designs with drag-and-drop plants, structures, and creative coloring modes. Use Zen mode to relax while coloring, or Creative mode for full artistic control.",
    icon: Palette,
    color: "from-purple-500 to-pink-600",
    position: "center",
    action: "Click 'My Gardens' → 'Design Studio'",
    tip: "Try the 'Bring to Life' button to see your garden animate!"
  },
  {
    id: "identify",
    title: "Plant Identifier",
    description: "Snap a photo of any plant and our smart system will identify it instantly! Get care tips, companion planting suggestions, and learn if it's safe for pets.",
    icon: Camera,
    color: "from-amber-500 to-orange-600",
    position: "center",
    action: "Click your profile → 'Identify Plant'",
    tip: "Works with flowers, vegetables, trees, and even weeds!"
  },
  {
    id: "learn",
    title: "Learn & Earn Achievements",
    description: "Level up from Seedling to Master Agronomist! Complete challenges, earn XP, and learn real agricultural STEM concepts like crop rotation, companion planting, and soil science.",
    icon: Trophy,
    color: "from-yellow-500 to-amber-600",
    position: "center",
    action: "Click 'Learning & Achievements' in your profile",
    tip: "24 achievements across 5 career phases await you!"
  },
  {
    id: "complete",
    title: "You're Ready to Grow!",
    description: "That's it! You now know the essentials. Start by exploring world garden templates or create your own design from scratch. Happy gardening!",
    icon: Sparkles,
    color: "from-green-500 to-teal-600",
    position: "center",
    highlight: null
  }
];

const GuidedTour = ({ onComplete, forceShow = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      setCurrentStep(0);
      return;
    }
    
    // Check if user has seen the tour
    const hasSeenTour = localStorage.getItem("rootspace_tour_completed");
    if (!hasSeenTour) {
      // Small delay before showing tour
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);
  
  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const completeTour = () => {
    localStorage.setItem("rootspace_tour_completed", "true");
    setIsVisible(false);
    onComplete?.();
  };
  
  const skipTour = () => {
    localStorage.setItem("rootspace_tour_completed", "true");
    setIsVisible(false);
    onComplete?.();
  };
  
  const step = TOUR_STEPS[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;
  
  if (!isVisible) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        data-testid="guided-tour"
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={skipTour}
        />
        
        {/* Tour Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative z-10 w-full max-w-lg"
        >
          <Card className="overflow-hidden shadow-2xl border-0">
            {/* Progress Bar */}
            <div className="h-1 bg-gray-200">
              <motion.div 
                className={`h-full bg-gradient-to-r ${step.color}`}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            
            {/* Header with Icon */}
            <div className={`bg-gradient-to-r ${step.color} p-6 text-white relative overflow-hidden`}>
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full border-4 border-white" />
                <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full border-4 border-white" />
              </div>
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <StepIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-medium">
                      Step {currentStep + 1} of {TOUR_STEPS.length}
                    </p>
                    <h2 className="text-xl font-bold">{step.title}</h2>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={skipTour}
                  className="text-white/70 hover:text-white hover:bg-white/20"
                  data-testid="tour-skip"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <CardContent className="p-6">
              {/* Description */}
              <p className="text-gray-600 leading-relaxed mb-4">
                {step.description}
              </p>
              
              {/* Action hint */}
              {step.action && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4 border border-gray-100">
                  <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    How to find it:
                  </p>
                  <p className="text-sm text-gray-600 mt-1 ml-6">{step.action}</p>
                </div>
              )}
              
              {/* Pro tip */}
              {step.tip && (
                <div className="bg-amber-50 rounded-lg p-3 mb-4 border border-amber-100">
                  <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Pro tip:
                  </p>
                  <p className="text-sm text-amber-600 mt-1 ml-6">{step.tip}</p>
                </div>
              )}
              
              {/* Step indicators */}
              <div className="flex justify-center gap-2 mb-6">
                {TOUR_STEPS.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentStep 
                        ? "w-6 bg-primary" 
                        : index < currentStep 
                          ? "bg-primary/50" 
                          : "bg-gray-300"
                    }`}
                    data-testid={`tour-dot-${index}`}
                  />
                ))}
              </div>
              
              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="gap-1"
                  data-testid="tour-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                
                {currentStep < TOUR_STEPS.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    className={`bg-gradient-to-r ${step.color} hover:opacity-90 gap-1`}
                    data-testid="tour-next"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={completeTour}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90 gap-1"
                    data-testid="tour-complete"
                  >
                    <Sparkles className="w-4 h-4" />
                    Start Gardening!
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Skip link */}
          <p className="text-center text-white/70 text-sm mt-4">
            Press Escape or click outside to skip the tour
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Button to restart the tour
export const RestartTourButton = ({ onClick }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={() => {
      localStorage.removeItem("rootspace_tour_completed");
      onClick?.();
    }}
    className="gap-2"
    data-testid="restart-tour-btn"
  >
    <RotateCcw className="w-4 h-4" />
    Replay Tour
  </Button>
);

export default GuidedTour;
